// Icon set (SPEC §3 + the paths used across the prototype). viewBox 24, stroke 2, round caps/joins.
// <Icon name="flame" size={20} />  colour = currentColor unless `color` is set; `fill` for solid icons (play, lit flame).
import type { JSX } from 'preact';

/** A shape is a path string, or a circle / rect descriptor. Rendered fresh on every use. */
type Shape = string | { c: [number, number, number] } | { r: [number, number, number, number, number] };
const p = (d: string): Shape => d;

export const ICONS = {
  play: [p('M8 5.5v13l10-6.5z')],
  lifebuoy: [{ c: [12, 12, 8.5] }, { c: [12, 12, 3.5] }, p('M6 6l3.5 3.5M14.5 14.5L18 18M18 6l-3.5 3.5M9.5 14.5L6 18')],
  flame: [p('M12 3c1 3.5 5 5.5 5 10a5 5 0 0 1-10 0c0-2.2 1.2-3.6 2.4-4.6.2 1.6 1 2.6 2.1 2.9C11 9 11.2 6 12 3z')],
  camera: [p('M4 8h3l2-3h6l2 3h3v11H4z'), { c: [12, 13, 3.5] }],
  plus: [p('M12 5v14M5 12h14')],
  trash: [p('M5 7h14M10 7V5h4v2M7 7l1 13h8l1-13')],
  check: [p('M5 12.5l4.5 4.5L19 7.5')],
  close: [p('M6 6l12 12M18 6L6 18')],
  back: [p('M15 5l-7 7 7 7')],
  chevron: [p('M9 5l7 7-7 7')],
  mic: [{ r: [9, 3, 6, 11, 3] }, p('M5 11a7 7 0 0 0 14 0'), p('M12 18v3')],
  lock: [{ r: [5, 11, 14, 9, 2] }, p('M8 11V8a4 4 0 0 1 8 0v3')],
  people: [{ c: [9, 8, 3.5] }, p('M2.5 20c.7-3.4 3.2-5.5 6.5-5.5s5.8 2.1 6.5 5.5'), p('M16 4.6a3.5 3.5 0 0 1 0 6.8M18 14.8c2 .6 3.2 2.4 3.5 5.2')],
  shield: [p('M12 3l7 3v6c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z')],
  chat: [p('M4 5h16v11H9l-5 4z')],
  calendar: [{ r: [4, 5, 16, 15, 2] }, p('M4 10h16M9 3v4M15 3v4')],
  home: [p('M4 20V9l8-5 8 5v11'), p('M9 20v-6h6v6')],
  person: [{ c: [12, 8, 4] }, p('M4 21c1-4 4-6 8-6s7 2 8 6')],
  gift: [{ r: [4, 9, 16, 11, 1.5] }, p('M3 9h18v-3H3zM12 6v14M12 6c-1.5-3-5-3-5-.5S11 6 12 6zM12 6c1.5-3 5-3 5-.5S13 6 12 6z')],
  sound: [p('M5 9v6h4l5 4V5L9 9z'), p('M17 9a4 4 0 0 1 0 6')],
  soundOff: [p('M5 9v6h4l5 4V5L9 9z'), p('M17 9l4 6M21 9l-4 6')],
  star: [p('M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.8-5.2 2.8 1-5.8-4.3-4.1 5.9-.9z')],
  send: [p('M12 19V5M6 11l6-6 6 6')],
  bell: [p('M6 17h12l-1.5-2V11a4.5 4.5 0 0 0-9 0v4zM10 20h4')],
  moon: [p('M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z')],
  arrowRight: [p('M5 12h14M13 6l6 6-6 6')],
  menu: [p('M4 6h16M4 12h16M4 18h16')],
  film: [{ r: [3, 6, 13, 12, 2] }, p('M16 10l5-3v10l-5-3z')],
  photo: [{ r: [3, 5, 18, 14, 2] }, p('M3 15l5-4 4 3 3-2 6 5')],
  file: [p('M7 3h7l5 5v13H7z'), p('M14 3v5h5')],
  share: [p('M12 15V3M7 8l5-5 5 5M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6')],
  edit: [p('M4 20h4L19 9l-4-4L4 16v4z')],
  headphones: [p('M4 15v-3a8 8 0 0 1 16 0v3'), { r: [3, 14, 4, 6, 1.5] }, { r: [17, 14, 4, 6, 1.5] }],
  monitor: [{ r: [3, 4, 18, 12, 2] }, p('M8 20h8M12 16v4')],
  pin: [p('M9 3h6l-1 6 3 3H7l3-3z'), p('M12 15v6')],
  music: [p('M9 18V5l12-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z')],
  book: [p('M2 5c3-1.5 7-1.5 10 1 3-2.5 7-2.5 10-1v14c-3-1.5-7-1.5-10 1-3-2.5-7-2.5-10-1zM12 6v14')],
  flag: [p('M5 21V4M5 4h11l-2 4 2 4H5')],
  mountain: [p('M3 20l6-11 4 6 3-4 5 9z')],
  compass: [p('M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18zM15.5 8.5l-2 5-5 2 2-5z')],
  // O2-O3 option icons (XOnb)
  briefcase: [p('M4 8h16v11H4z'), p('M9 8V5h6v3')],
  mail: [p('M4 6h16v12H4z'), p('M4 7l8 6 8-6')],
  pulse: [p('M3 12h4l3-7 4 14 3-7h4')],
  bookOpen: [p('M4 5h7a2 2 0 0 1 2 2v12a2 2 0 0 0-2-2H4z'), p('M20 5h-7a2 2 0 0 0-2 2v12a2 2 0 0 1 2-2h7z')],
  start: [p('M5 21V4h11l-2 4 2 4H5')],
  wave: [p('M3 12c3-6 6 6 9 0s6 6 9 0')],
  finish: [p('M5 21V4l7 3 7-3v10l-7 3-7-3')],
  // S stuck reasons (QS1_Stuck R): energy, distract, worry, time ("big" = mountain, "start" = compass)
  battery: [p('M4 8h14v8H4zM18 11h2v2h-2M6.5 10.5v3')],
  phone: [p('M8 3h8v18H8zM11 18h2M3 9l2 1M3 14l2-1M21 9l-2 1M21 14l-2-1')],
  cloud: [p('M7 18a4 4 0 0 1-.6-7.96A6 6 0 0 1 18 9a4.5 4.5 0 0 1-.5 9z')],
  clock: [p('M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18zM12 7v5l3 2')],
  // phone v2 (F_Chat / F_Edit / F_Me tz)
  pencil: [p('M4 20h4L19 9l-4-4L4 16z'), p('M13.5 6.5l4 4')],
  search: [{ c: [11, 11, 6.5] }, p('M16 16l4 4')],
  arrow: [p('M5 12h14M13 6l6 6-6 6')],
  list: [p('M9 6h11M9 12h11M9 18h11'), { c: [4.5, 6, 0.8] }, { c: [4.5, 12, 0.8] }, { c: [4.5, 18, 0.8] }],
} satisfies Record<string, Shape[]>;

export type IconName = keyof typeof ICONS;

export interface IconProps {
  name: IconName;
  size?: number;
  /** Stroke colour (default currentColor). */
  color?: string;
  /** Fill colour (default none). */
  fill?: string;
  strokeWidth?: number;
  class?: string;
  style?: JSX.CSSProperties;
  /** Accessible name. Without it the icon is decorative (aria-hidden). */
  label?: string;
}

export function Icon({ name, size = 24, color = 'currentColor', fill = 'none', strokeWidth = 2, class: cls, style, label }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24" width={size} height={size} class={cls}
      style={{ flex: 'none', fill, stroke: color, strokeWidth, strokeLinecap: 'round', strokeLinejoin: 'round', ...style }}
      aria-hidden={label ? undefined : 'true'} role={label ? 'img' : undefined} aria-label={label}
    >
      {(ICONS[name] as Shape[]).map((sh, i) => (typeof sh === 'string'
        ? <path key={i} d={sh} />
        : 'c' in sh ? <circle key={i} cx={sh.c[0]} cy={sh.c[1]} r={sh.c[2]} />
        : <rect key={i} x={sh.r[0]} y={sh.r[1]} width={sh.r[2]} height={sh.r[3]} rx={sh.r[4]} />))}
    </svg>
  );
}
